import { Component, computed, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ButtonModule } from 'primeng/button';
import { LocalizationService } from '../../../core/localization/localization.service';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import {
  FORM_STOREFRONT_DISPLAY_MODES,
  FormSchemaModel
} from '../../../shared/dynamic-form/form-schema.models';
import { FormRuntimeRendererComponent } from '../../../shared/dynamic-form/form-runtime-renderer.component';

type PreviewDevice = 'desktop' | 'tablet' | 'mobile';
type PreviewDisplayMode = 'fullPage' | 'modal' | 'inlineEmbed';

@Component({
  standalone: true,
  selector: 'ain-form-builder-preview',
  imports: [
    FormsModule,
    ButtonModule,
    CheckboxModule,
    SelectModule,
    SelectButtonModule,
    InfoHintComponent,
    FormRuntimeRendererComponent
  ],
  template: `
    <section class="preview-shell">
      <ain-info-hint
        [titleKey]="'dynamic.builder.preview.infoTitle'"
        [descriptionKey]="'dynamic.builder.preview.infoDescription'"
        [bulletKeys]="[
          'dynamic.builder.preview.infoBulletRuntime',
          'dynamic.builder.preview.infoBulletLive'
        ]" />

      <div class="preview-toolbar">
        <p-selectButton
          [options]="deviceOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="device()"
          (ngModelChange)="device.set($event)"
          [allowEmpty]="false"
          [attr.aria-label]="t('dynamic.builder.preview.device')" />

        @if (displayModeOptions().length > 1) {
          <p-select
            [options]="displayModeOptions()"
            optionLabel="label"
            optionValue="value"
            [ngModel]="displayMode()"
            appendTo="body"
            [placeholder]="t('dynamic.builder.preview.displayMode')"
            (ngModelChange)="displayMode.set($event)" />
        }

        <p-select
          [options]="previewCultureOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="previewCulture()"
          appendTo="body"
          [attr.aria-label]="t('dynamic.builder.preview.previewCulture')"
          (ngModelChange)="previewCulture.set($event)" />

        <div class="preview-script-toggle">
          <p-checkbox
            [binary]="true"
            [ngModel]="runScripts()"
            (ngModelChange)="runScripts.set($event)"
            [inputId]="runScriptsInputId" />
          <label [for]="runScriptsInputId">{{ t('dynamic.builder.preview.runScripts') }}</label>
        </div>
      </div>

      <div class="preview-stage" [class]="'mode-' + displayMode()" [style.max-width.px]="deviceWidth()">
        <div class="preview-frame">
              <ain-form-runtime-renderer
                [schema]="schema()"
                mode="preview"
                [culture]="previewCulture()"
                [lookupSearchEnabled]="true"
                [showRuntimeInfoHint]="false"
            [showSubmitFooter]="false"
            [scriptsEnabled]="runScripts()" />

          <footer class="preview-footer">
            <button pButton type="button" [label]="t('dynamic.builder.preview.submit')" [disabled]="true"></button>
            <small>{{ t('dynamic.builder.preview.submitNote') }}</small>
          </footer>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .preview-shell { display: grid; gap: 12px; }
    .preview-toolbar { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
    .preview-script-toggle { display: inline-flex; align-items: center; gap: 8px; }
    .preview-stage {
      margin-inline: auto;
      width: 100%;
      transition: max-width 0.2s ease;
    }
    .preview-stage.mode-modal {
      padding: 24px;
      background: rgba(15, 23, 42, 0.45);
      border-radius: 12px;
    }
    .preview-stage.mode-inlineEmbed {
      border: 2px dashed #cbd5e1;
      border-radius: 12px;
      padding: 12px;
    }
    .preview-frame {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 16px;
      display: grid;
      gap: 12px;
      box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
    }
    .preview-stage.mode-modal .preview-frame {
      max-height: 70vh;
      overflow: auto;
    }
    .preview-footer {
      display: grid;
      gap: 6px;
      padding-block-start: 12px;
      border-block-start: 1px solid #e2e8f0;
    }
    .preview-footer small { color: #64748b; }
  `]
})
export class FormBuilderPreviewComponent {
  private readonly localization = inject(LocalizationService);

  readonly schema = input.required<FormSchemaModel>();

  readonly device = signal<PreviewDevice>('desktop');
  readonly displayMode = signal<PreviewDisplayMode>('fullPage');
  readonly previewCulture = signal<string>(this.localization.activeCulture().startsWith('fa') ? 'fa-IR' : 'en-US');
  readonly runScripts = signal(true);
  readonly runScriptsInputId = 'form-builder-preview-run-scripts';

  readonly deviceOptions = computed(() => [
    { label: this.t('dynamic.builder.preview.deviceDesktop'), value: 'desktop' },
    { label: this.t('dynamic.builder.preview.deviceTablet'), value: 'tablet' },
    { label: this.t('dynamic.builder.preview.deviceMobile'), value: 'mobile' }
  ]);

  readonly previewCultureOptions = computed(() => [
    { label: 'English', value: 'en-US' },
    { label: 'فارسی', value: 'fa-IR' }
  ]);

  readonly displayModeOptions = computed(() => {
    const modes = this.schema().publicAccess?.storefront?.displayModes ?? ['fullPage'];
    const allowed = new Set(modes);
    return FORM_STOREFRONT_DISPLAY_MODES
      .filter(mode => allowed.has(mode))
      .map(mode => ({ label: this.t(`dynamic.builder.preview.display.${mode}`), value: mode }));
  });

  readonly deviceWidth = computed(() => {
    switch (this.device()) {
      case 'tablet':
        return 768;
      case 'mobile':
        return 375;
      default:
        return undefined;
    }
  });

  t(key: string): string {
    return this.localization.translate(key);
  }
}
